import {action, observable, makeObservable, runInAction, autorun} from "mobx";
import {AppConfiguration} from "./config/app-configuration";
import EsriMap from "@arcgis/core/Map";
import {WebMapConfiguration} from "./config/web-map-configuration";
import Portal from "@arcgis/core/portal/Portal";
import Basemap from "@arcgis/core/Basemap";
import {PortalBasemapsSourceConfiguration} from "./config/basemap-gallery-configuration";
import {FormatUtils} from "../utils/format-utils";
import {PathUtils} from "../utils/path-utils";
import _ from "lodash";
// import {ArcgisReferenceElement} from "@arcgis/map-components/dist/utils/component-utils";

export interface BasemapProps {
    id: string;
    title?: string;
    tags?: string[];
}

export class MapState {
    status: string;
    map: EsriMap | undefined;
    // mapComponent: ArcgisReferenceElement | undefined;
    view: __esri.MapView | __esri.SceneView | undefined;
    layers = observable.map<string, __esri.Layer>();
    groupLayers = observable.map<string, __esri.Layer>();
    visibleFilterlayers = observable.map<string, __esri.Layer>();
    layerViews = observable.map<string, __esri.FeatureLayerView>();
    highlightedFeature: __esri.Handle | null;
    activeLayers = observable.map<string,number[]>();
    activeLayerMetauids: string[];
    selectedFeature: __esri.Graphic | null;
    basemap: BasemapProps | null;
    basemapOpacity: number;
    defaultBasemapTitle: string;
    basemapAttribution: string;
    webMap: WebMapConfiguration;
    tocReady = false;

    featureInfo: string;

    appConfig: AppConfiguration;
    defaultBasemap: BasemapProps;

    constructor() {
        makeObservable(this, {
            status: observable,
            map: observable,
            // mapComponent: observable,
            view: observable,
            layers: observable,
            groupLayers: observable,
            visibleFilterlayers: observable,
            layerViews: observable,
            highlightedFeature: observable,
            activeLayers: observable,
            activeLayerMetauids: observable,
            selectedFeature: observable,
            basemap: observable,
            basemapOpacity: observable,
            defaultBasemapTitle: observable,
            basemapAttribution: observable,
            webMap: observable,
            tocReady: observable,
            featureInfo: observable,

            setStatus: action,
            setMap: action,
            // setMapComponent: action,
            setView: action,
            setFeatureInfo: action,
            clearFeatureInfo: action,
            addLayer: action,
            removeLayer: action,
            addGroupLayer: action,
            removeGroupLayer: action,
            addVisibleFilterLayer: action,
            removeVisibleFilterLayer: action,
            addLayerView: action,
            removeLayerView: action,
            selectFeature: action,
            setActiveLayers: action,
            setActiveLayerMetauids: action,
            highlightFeature: action,
            unhighlightFeature: action,
            setBasemap: action,
            setBasemapById: action,
            removeBasemap: action,
            setBasemapOpacity: action,
            setBasemapAttribution: action,
            setDefaultBasemapTitle: action,
            setDefaultBasemap: action,
            setWebMap: action,
            setTocReady: action
        });
        this.setStatus("loading");
        this.setBasemapOpacity(100);
    }

    setStatus(status: string): void {
        this.status = status;
    }

    setMap(map?: EsriMap): void {
        this.map = map;
    }

    // setMapComponent(mapComponent?: ArcgisReferenceElement): void {
    //     this.mapComponent = mapComponent;
    // }

    setView(view?: __esri.MapView | __esri.SceneView): void {
        this.view = view;
    }

    setFeatureInfo(info: string): void {
        this.featureInfo = info;
    }
    clearFeatureInfo(): void {
        this.featureInfo = "";
    }

    addLayer(key: string, layer: __esri.Layer): void {
        this.layers.set(key, layer);
    }
    removeLayer(key: string): void {
        this.layers.delete(key);
    }
    addGroupLayer(key: string, layer: __esri.Layer): void {
        this.groupLayers.set(key, layer);
    }
    removeGroupLayer(key: string): void {
        this.groupLayers.delete(key);
    }
    addVisibleFilterLayer(key: string, layer: __esri.Layer): void {
        this.visibleFilterlayers.set(key, layer);
    }
    removeVisibleFilterLayer(key: string): void {
        this.visibleFilterlayers.delete(key);
    }

    addLayerView(key: string, layerView: __esri.FeatureLayerView): void {
        this.layerViews.set(key, layerView);
    }
    removeLayerView(key: string): void {
        this.layerViews.delete(key);
    }

    selectFeature(feature: __esri.Graphic | null): void {
        this.selectedFeature = feature;
    }
    setActiveLayers(layers: Map<string,number[]>): void {
        this.activeLayers.replace(layers);
    }
    setActiveLayerMetauids(metauids: string[]): void {
        if (_.isEqual(this.activeLayerMetauids, metauids)) {
            return;
        }
        this.activeLayerMetauids = metauids;
    }

    /**
     * Highlighted feature in the map
     * At the moment, only one feature can be highlighted at the same time
     *
     * @param feature
     */
    highlightFeature(feature: __esri.Handle): void {
        this.highlightedFeature = feature;
    }
    /**
     * Remove highlight from feature
     */
    unhighlightFeature(): void {
        if (!this.highlightedFeature) {
            return;
        }
        this.highlightedFeature.remove();
        this.highlightedFeature = null;
    }

    /**
     * Set Basemap
     *
     * @param {string} [urlOrTag]: A Tag or the URL to the REST endpoint of the map service.
     * @param {string} [title]: Only allowed if layers is a string.
     * @param {boolean} [asDefault]: Wird als Default Basemap gesetzt
     */
    setBasemap(urlOrTag: string, title?: string, asDefault?: boolean): void {
        if (urlOrTag.indexOf("http") === -1) {
            const portal = new Portal();
            portal.load().then(()=> {
                runInAction(()=>{
                    const {basemapGallery} = this.appConfig;
                    // TODO: Query sollte auch als Objekt funktionieren
                    portal.fetchBasemaps(basemapGallery?.source
                        ? (basemapGallery?.source as PortalBasemapsSourceConfiguration).query
                        : undefined).then((basemaps)=>{
                        let basemapItem;
                        // Grundkarte anhand Tag suchen
                        if(urlOrTag!==""){
                            basemapItem = basemaps.find((b) => b.portalItem?.tags?.includes(urlOrTag));
                            // Grundkarte nach Titel suchen
                        } else if (title){
                            basemapItem = basemaps.find((b) => FormatUtils.formatTitle(b.portalItem?.title)
                                === FormatUtils.formatTitle(title)
                                && b.portalItem?.tags?.filter(
                                    (tag) => basemapGallery.excludeTags?.includes(tag)).length === 0);
                        }
                        if (basemapItem && this.basemap?.id !== basemapItem.portalItem?.id
                            && typeof basemapItem.portalItem?.id ==="string") {
                            const itemId = basemapItem.portalItem?.id;
                            const basemap: BasemapProps = {
                                id: itemId,
                                title: FormatUtils.formatTitle(basemapItem.portalItem.title),
                                tags: (basemapItem.portalItem?.tags)? basemapItem.portalItem?.tags: []
                            };
                            this.setBasemapById(basemap);
                            if(asDefault===true && basemap.title){
                                this.setDefaultBasemap(basemap);
                            }
                            autorun((reaction: any) => {
                                if (!this.map) {
                                    return;
                                }
                                reaction.dispose(); // stop autorun
                                runInAction(()=>{
                                    if(this.map && this.basemap) {
                                        const newBasemap = new Basemap({
                                            portalItem: {
                                                id: this.basemap.id
                                            }
                                        });
                                        this.map.basemap?.cancelLoad();
                                        newBasemap.loadAll().then(() => {
                                            if(this.map) {
                                                this.map.basemap = newBasemap;
                                                this.map.basemap?.baseLayers.forEach((layer: __esri.Layer) => {
                                                    layer.opacity = this.basemapOpacity / 100;
                                                });
                                            }
                                        });
                                    }
                                });
                            });
                        }
                    });
                });
            });
        } else {
            this.basemap = {
                id: urlOrTag,
                title: title? title: undefined
            };
        }
    }
    removeBasemap(): void {
        if(this.map?.basemap){
            this.map.basemap.destroy();
        }
        this.basemap=null;
        this.setBasemapOpacity(0);
    }

    /**
     * Set Basemap
     *
     * @param {string|BasemapProps} [basemapId]: Portal Id of basemap item.
     * @param {string} [title]
     * @param {array} [tags]
     */
    setBasemapById(basemapId: string | BasemapProps, title?: string, tags?: string[]): void {
        if (typeof basemapId !== "string") {
            this.basemap = basemapId as BasemapProps;
        } else {
            this.basemap = {
                id: basemapId,
                title: title? title: undefined,
                tags: tags? tags: undefined,
            };
        }
    }
    setBasemapOpacity(basemapOpacity: number): void {
        this.basemapOpacity = basemapOpacity;
    }
    setBasemapAttribution(attribution: string): void {
        this.basemapAttribution = attribution;
    }
    setDefaultBasemapTitle(title: string): void {
        this.defaultBasemapTitle = FormatUtils.formatTitle(title);
    }
    setDefaultBasemap(basemap: BasemapProps): void {
        this.defaultBasemap = basemap;
        if(basemap.title){
            if(this.defaultBasemapTitle !== FormatUtils.formatTitle(basemap.title)){
                this.defaultBasemapTitle = FormatUtils.formatTitle(basemap.title);
            }
            this.defaultBasemap.title = this.defaultBasemapTitle;
        }
    }

    setWebMap(webMap: WebMapConfiguration): void {
        if (typeof this.webMap !== "undefined" && this.webMap.portalItem?.id === webMap.portalItem?.id) {
            return;
        }
        this.webMap = webMap;
    }
    setTocReady(){
        this.tocReady = true;
    }
    setAppConfig(appConfig: AppConfiguration): void {
        this.appConfig = appConfig;
    }

    /**
     * Get layer views by METAUID and layerIds
     * Flatten group layers to get all layer views
     * @param metauid
     * @param layerIds
     */
    getLayerViewsByMetauid(metauid: string, layerIds?: number[]): __esri.LayerView[] {
        if (!this.view) {
            // eslint-disable-next-line no-console
            console.error("View not available");
            return [];
        }
        const layerViews = this.view.layerViews.filter((layerView: __esri.LayerView) => {
            return !!(
                (
                    (layerView.layer as any).metauid === metauid ||
                    PathUtils.getMetaId(PathUtils.getLayerPath(layerView.layer)) === metauid
                ) &&
                (
                    typeof layerIds === "undefined" ||
                    // feature layer
                    layerIds?.includes((layerView.layer as any).layerId) ||
                    // map image layer
                    (layerView.layer as __esri.MapImageLayer).sublayers?.map((l: any) => l.id)
                        .some((id: number) => layerIds?.includes(id))
                )
            );
        });
        return layerViews.toArray();
    }

    /**
     * Get layers by METAUID and layerIds
     * Flatten group layers to get all layer views
     * @param metauid
     * @param layerIds
     */
    getLayersByMetauid(metauid: string, layerIds?: number[]): __esri.Layer[] {
        if (!this.view) {
            // eslint-disable-next-line no-console
            console.error("View not available");
            return [];
        }
        const layers = this.map?.allLayers.flatten((subL: __esri.Layer) => {
            return (subL as __esri.MapImageLayer).allSublayers as unknown as __esri.Collection<__esri.Layer>;
        }).filter((layer: __esri.Layer) => {
            const layerMetauid = (layer as any).metauid || PathUtils.getMetaId(PathUtils.getLayerPath(layer));
            return !!(
                layerMetauid === metauid &&
                (
                    !layerIds ||
                    // feature layer
                    layerIds?.includes((layer as any).layerId) ||
                    // map image layer
                    (layer as __esri.MapImageLayer).sublayers?.map((l: any) => l.id).some((id: number) =>
                        layerIds?.includes(id))
                )
            );
        });
        return layers?.toArray() || [];
    }
    getLayerByMetauid(metauid: string, layerId?: number): __esri.Layer | void {
        if (!this.view) {
            // eslint-disable-next-line no-console
            console.error("View not available");
            return;
        }
        const layer = this.map?.allLayers.flatten((subL: __esri.Layer) =>
            (subL as __esri.MapImageLayer)?.allSublayers as unknown as __esri.Collection<__esri.Layer>)
            .find((l: __esri.Layer) => {
                let lMetauid = (l as any).metauid;
                let lId: number | null = null;
                if (!lMetauid){
                    const url = PathUtils.getLayerPath(l);
                    if (typeof url === "string") {
                        lMetauid = PathUtils.getMetaId(url);
                        lId = PathUtils.getLayerId(url);
                    }
                }
                if (lMetauid) {
                    if(metauid === lMetauid &&
                        (typeof layerId !== "number" || lId === layerId)) {
                        return true;
                    }
                }
                return false;
            });
        return layer;
    }
}
